import * as THREE from 'three';
import type { PixelationPipeline } from '../../render/Pixelation';
import type { SfxRuntime } from '../../render/Sfx';
import { motionBlurPrefs, computeMotionBlurStrength } from '../../render/MotionBlurPrefs';
import type { ResolvedStyle } from '../../render/OverrideResolver';
import type { System } from '../ServiceLocator';

/**
 * Owns the post-processing pipeline: pixelation, bloom (WebGL or
 * WebGPU variant via SfxRuntime), and the per-frame motion blur
 * smoothing state.
 *
 * Construction is still done in bootstrap.ts (because bloom build
 * depends on the renderer mode + an async import for WebGPU). The
 * pipelines are then handed to this system which owns lifecycle and
 * style + motion-blur driving.
 */
export class PostFXSystem implements System {
  readonly name = 'PostFXSystem';

  private motionBlurStrengthSmoothed = 0;
  private readonly blurForward = new THREE.Vector3();

  constructor(
    readonly pixelation: PixelationPipeline,
    readonly bloom: SfxRuntime | null,
  ) {}

  get hasBloom(): boolean { return this.bloom != null; }

  setSize(w: number, h: number): void {
    this.bloom?.setSize(w, h);
  }

  setPixelRatio(scale: number): void {
    this.bloom?.setPixelRatio(scale);
  }

  setRenderScale(scale: number): void {
    this.bloom?.setRenderScale(scale);
  }

  syncPixelation(enabled: boolean): void {
    this.bloom?.setPixelation(enabled, this.pixelation.config.width, this.pixelation.config.height);
  }

  applyStyle(resolvedStyle: ResolvedStyle): void {
    this.bloom?.setSfxStyle({
      bloom: {
        intensity: resolvedStyle.sfx.bloom.intensity,
        radius: resolvedStyle.sfx.bloom.radius,
        threshold: resolvedStyle.sfx.bloom.threshold,
      },
      selectiveBloom: resolvedStyle.sfx.selectiveBloom,
      colorGrade: {
        enabled: resolvedStyle.sfx.colorGrade.enabled,
        lift: resolvedStyle.sfx.colorGrade.lift,
        gain: resolvedStyle.sfx.colorGrade.gain,
        saturation: resolvedStyle.sfx.colorGrade.saturation,
        contrast: resolvedStyle.sfx.colorGrade.contrast,
        gamma: resolvedStyle.sfx.colorGrade.gamma,
      },
      vignette: resolvedStyle.sfx.vignette,
      chromaticAberration: resolvedStyle.sfx.chroma,
      motion: {
        samples: resolvedStyle.sfx.motion.radialSamples,
        falloff: resolvedStyle.sfx.motion.falloff,
        chromaticAmount: resolvedStyle.sfx.motion.chromaticAmount,
      },
    });
    this.bloom?.setColorGradeEnabled(resolvedStyle.sfx.colorGrade.enabled);
  }

  /** Adaptive-quality hook: zero motion blur when render load is reduced. */
  reduceLoad(): void {
    this.bloom?.setRadialBlurStrength(0);
    this.motionBlurStrengthSmoothed = 0;
  }

  /** Per-frame motion blur drive. Computes target strength from speed,
   *  smooths it with exponential ramp, applies to bloom, and tracks the
   *  camera-forward vanishing point. */
  updateMotionBlur(opts: {
    speed: number;
    maxSpeed: number;
    resolvedStyle: ResolvedStyle;
    visualFrameDt: number;
    camera: THREE.Camera;
  }): void {
    if (!this.bloom) return;
    const target = computeMotionBlurStrength(
      opts.speed,
      opts.maxSpeed,
      {
        styleEnabled: opts.resolvedStyle.sfx.motion.enabled,
        radialStrength: opts.resolvedStyle.sfx.motion.radialStrength,
        speedBandLow: 0.4,
        speedBandHigh: 1.0,
      },
      { enabled: motionBlurPrefs.isEnabled(), intensity: motionBlurPrefs.getIntensity() },
    );
    const alpha = 1 - Math.exp(-12 * Math.max(0.0001, opts.visualFrameDt));
    this.motionBlurStrengthSmoothed += (target - this.motionBlurStrengthSmoothed) * alpha;
    this.bloom.setRadialBlurStrength(this.motionBlurStrengthSmoothed);
    motionBlurPrefs.liveStrength = this.motionBlurStrengthSmoothed;
    const projected = this.blurForward.set(0, 0, -1)
      .applyQuaternion(opts.camera.quaternion)
      .add(opts.camera.position)
      .project(opts.camera);
    this.bloom.setRadialBlurCenter(
      THREE.MathUtils.clamp(projected.x * 0.5 + 0.5, 0, 1),
      THREE.MathUtils.clamp(projected.y * 0.5 + 0.5, 0, 1),
    );
  }

  /** Zero motion blur and clear the live-strength pref. Used when the
   *  effect should be off (disabled, paused, or non-racing modes). */
  zeroMotionBlur(): void {
    if (!this.bloom) return;
    this.motionBlurStrengthSmoothed = 0;
    this.bloom.setRadialBlurStrength(0);
    motionBlurPrefs.liveStrength = 0;
  }

  dispose(): void {
    this.bloom?.dispose();
  }

  /** Fields this system contributes to the window.__twin debug surface. */
  debugEntries(): Record<string, unknown> {
    return { bloom: this.bloom };
  }
}
